// src/components/superadmin/ActivityHeatmap.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { activityService } from '../../services/activityService'; 
import './ActivityHeatmap.css';

const ActivityHeatmap = ({ className = '' }) => {
  const [systemActivity, setSystemActivity] = useState({});
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState('all');
  const [selectedTimeRange, setSelectedTimeRange] = useState('current');

  // Real-time data states
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Track if we've received at least one payload from the subscriptions
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Check if compact mode
  const isCompact = className.includes('compact');

  const year = selectedTimeRange === 'current' ? new Date().getFullYear() : new Date().getFullYear() - 1;

  // Memoized calculations for performance
  const activitySummary = useMemo(() => {
    const totalDays = Object.keys(systemActivity).length;
    const totalActivities = Object.values(systemActivity).reduce((sum, count) => sum + count, 0);
    const avgDailyActivity = totalDays > 0 ? Math.round(totalActivities / totalDays) : 0;
    const maxDayActivity = Math.max(...Object.values(systemActivity), 0);
    const activeDays = Object.values(systemActivity).filter(count => count > 0).length;
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const totalDaysInYear = isLeap ? 366 : 365;
    
    return {
      totalActivities,
      avgDailyActivity,
      maxDayActivity,
      activeDays,
      totalDays: totalDaysInYear
    };
  }, [systemActivity, year]);

  // Set up real-time listeners via Service Layer
  useEffect(() => {
    const onData = (setter) => (data) => {
      setter(data);
      setHasLoadedOnce(true);
    };

    const unsubs = [
      activityService.subscribeToUsers(onData(setUsers)),
      activityService.subscribeToAssets(onData(setAssets)),
      activityService.subscribeToCompanies(onData(setCompanies)),
      activityService.subscribeToAuditLogs(onData(setAuditLogs)),
    ];

    return () => unsubs.forEach((u) => typeof u === 'function' && u());
  }, []);

  // Process data when it changes
  useEffect(() => {
    if (!hasLoadedOnce) return;
    try {
      processRealTimeActivity();
    } catch (err) {
      console.error(err);
      setError('Failed to process system activity data');
    } finally {
      setLoading(false);
    }
  }, [users, assets, companies, auditLogs, selectedTimeRange, hasLoadedOnce]);

  const processRealTimeActivity = () => {
    try {
      const dailyActivityCounts = {};
      const userActivityStats = {};
      const companyActivityStats = {};

      // Process user creation/update activities
      users.forEach(user => {
        const userCreatedAt = user.createdAt || user.updatedAt;
        if (userCreatedAt) {
          try {
            const createdDate = userCreatedAt.toDate ? userCreatedAt.toDate() : new Date(userCreatedAt);
            if (createdDate.getFullYear() === year) {
              const dateString = createdDate.toISOString().split('T')[0];
              dailyActivityCounts[dateString] = (dailyActivityCounts[dateString] || 0) + 1;

              if (!userActivityStats[user.id]) {
                userActivityStats[user.id] = {
                  email: user.email || user.displayName || 'Unknown User',
                  count: 0,
                  lastActive: createdDate
                };
              }
              userActivityStats[user.id].count += 1;
              
              if (user.companyId) {
                if (!companyActivityStats[user.companyId]) {
                  const companyInfo = companies.find(c => c.id === user.companyId || c.companyId === user.companyId);
                  companyActivityStats[user.companyId] = {
                    name: companyInfo?.name || user.company || 'Unknown Company',
                    count: 0,
                    lastActive: createdDate
                  };
                }
                companyActivityStats[user.companyId].count += 1;
                if (createdDate > companyActivityStats[user.companyId].lastActive) {
                  companyActivityStats[user.companyId].lastActive = createdDate;
                }
              }
            }
          } catch (error) {
            console.warn('Error processing user date:', error);
          }
        }
      });

      // Process asset creation activities
      assets.forEach(asset => {
        const assetCreatedAt = asset.createdAt || asset.updatedAt;
        if (assetCreatedAt) {
          try {
            const createdDate = assetCreatedAt.toDate ? assetCreatedAt.toDate() : new Date(assetCreatedAt);
            if (createdDate.getFullYear() === year) {
              const dateString = createdDate.toISOString().split('T')[0];
              dailyActivityCounts[dateString] = (dailyActivityCounts[dateString] || 0) + 2; 

              if (asset.companyId && companyActivityStats[asset.companyId]) {
                companyActivityStats[asset.companyId].count += 2;
                if (createdDate > companyActivityStats[asset.companyId].lastActive) {
                  companyActivityStats[asset.companyId].lastActive = createdDate;
                }
              }
            }
          } catch (error) {
            console.warn('Error processing asset date:', error);
          }
        }
      });

      // Process company creation activities
      companies.forEach(company => {
        const companyCreatedAt = company.createdAt || company.updatedAt;
        if (companyCreatedAt) {
          try {
            const createdDate = companyCreatedAt.toDate ? companyCreatedAt.toDate() : new Date(companyCreatedAt);
            if (createdDate.getFullYear() === year) {
              const dateString = createdDate.toISOString().split('T')[0];
              dailyActivityCounts[dateString] = (dailyActivityCounts[dateString] || 0) + 3; 
            }
          } catch (error) {
            console.warn('Error processing company date:', error);
          }
        }
      });

      // Process audit logs if available
      auditLogs.forEach(log => {
        if (log.timestamp) {
          try {
            const logDate = log.timestamp.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
            if (logDate.getFullYear() === year) {
              const dateString = logDate.toISOString().split('T')[0];
              dailyActivityCounts[dateString] = (dailyActivityCounts[dateString] || 0) + 1;

              if (log.userEmail && log.userId) {
                if (!userActivityStats[log.userId]) {
                  userActivityStats[log.userId] = {
                    email: log.userEmail,
                    count: 0,
                    lastActive: logDate
                  };
                }
                userActivityStats[log.userId].count += 1;
                if (logDate > userActivityStats[log.userId].lastActive) {
                  userActivityStats[log.userId].lastActive = logDate;
                }
              }

              if (log.companyId) {
                if (!companyActivityStats[log.companyId]) {
                  const companyInfo = companies.find(c => c.id === log.companyId || c.companyId === log.companyId);
                  companyActivityStats[log.companyId] = {
                    name: companyInfo?.name || log.companyName || 'Unknown Company',
                    count: 0,
                    lastActive: logDate
                  };
                }
                companyActivityStats[log.companyId].count += 1;
                if (logDate > companyActivityStats[log.companyId].lastActive) {
                  companyActivityStats[log.companyId].lastActive = logDate;
                }
              }
            }
          } catch (error) {
            console.warn('Error processing audit log date:', error);
          }
        }
      });

      setSystemActivity(dailyActivityCounts);
      setUserStats({
        users: userActivityStats,
        companies: companyActivityStats
      });

    } catch (error) {
      console.error('Error processing real-time activity:', error);
      setError('Failed to process system activity data');
    }
  };

  // Memoize these so they don't compute twice per render
  const topUsers = useMemo(() => {
    return Object.entries(userStats.users || {})
      .sort(([,a], [,b]) => b.count - a.count)
      .slice(0, 5)
      .map(([userId, stats]) => ({ id: userId, ...stats }));
  }, [userStats.users]);

  const topCompanies = useMemo(() => {
    return Object.entries(userStats.companies || {})
      .sort(([,a], [,b]) => b.count - a.count)
      .slice(0, 5)
      .map(([companyId, stats]) => ({ id: companyId, ...stats }));
  }, [userStats.companies]);

  // Bar graph denominators (uses the max of the list, not the max of the day)
  const maxUserCount = Math.max(1, ...topUsers.map(u => u.count));
  const maxCompanyCount = Math.max(1, ...topCompanies.map(c => c.count));

  const getDayColor = (count) => {
    if (count === 0) return 'level-0';
    if (count <= 3) return 'level-1';
    if (count <= 8) return 'level-2';
    if (count <= 15) return 'level-3';
    if (count <= 25) return 'level-4';
    return 'level-5';
  };

  const generateHeatmapDays = () => {
    const days = [];
    
    const startDate = new Date(year, 0, 1);
    const startDay = startDate.getDay();
    const daysToSubtract = startDay === 0 ? 6 : startDay - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    const yearEnd = new Date(year, 11, 31);
    const endDate = new Date(yearEnd);
    const endDay = endDate.getDay();
    const daysToAdd = endDay === 0 ? 0 : 7 - endDay;
    endDate.setDate(endDate.getDate() + daysToAdd);

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateString = currentDate.toISOString().split('T')[0];
      const count = systemActivity[dateString] || 0;
      const dayOfWeek = currentDate.getDay();
      const isCurrentYear = currentDate.getFullYear() === year;
      const colorLevel = getDayColor(count);

      days.push(
        <div
          key={dateString}
          className={`activity-day ${colorLevel} ${
            dayOfWeek === 0 || dayOfWeek === 6 ? 'weekend' : ''
          } ${!isCurrentYear ? 'out-of-year' : ''}`}
          title={`${currentDate.toLocaleDateString()}: ${count} activities`}
          data-date={dateString}
          data-count={count}
          style={{ 
            backgroundColor: getBackgroundColor(count),
            width: '100%',
            aspectRatio: '1',
            borderRadius: '2px',
            minWidth: 0
          }}
        />
      );

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const getBackgroundColor = (count) => {
    if (count === 0) return '#ebedf0';
    if (count <= 3) return '#c6e48b';
    if (count <= 8) return '#7bc96f';
    if (count <= 15) return '#239a3b';
    if (count <= 25) return '#196127';
    return '#0d4016';
  };

  const generateMonthLabels = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => (
      <span key={index} className="month-label">{month}</span>
    ));
  };

  const handleRetry = () => {
    setError(null);
    processRealTimeActivity();
  };

  if (loading) {
    return (
      <div className={`system-activity-heatmap ${className}`}>
        <div className="activity-heatmap-loading">
          <div className="loading-spinner"></div>
          <p>Loading real-time system activity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`system-activity-heatmap ${className}`}>
        <div className="activity-heatmap-error">
          <div className="error-icon">⚠️</div>
          <p className="error-message">{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`system-activity-heatmap ${className}`} style={{ minWidth: 0, width: '100%' }}>
      {/* Header - Show only in non-compact mode */}
      {!isCompact && (
        <div className="heatmap-header">
          <div className="heatmap-title">
            <h3>Real-Time System Activity</h3>
            <p>Live activity based on users, assets, and system interactions</p>
          </div>
          
          <div className="heatmap-controls">
            <div className="time-range-selector">
              <button 
                className={selectedTimeRange === 'current' ? 'active' : ''}
                onClick={() => setSelectedTimeRange('current')}
              >
                {new Date().getFullYear()}
              </button>
              <button 
                className={selectedTimeRange === 'last' ? 'active' : ''}
                onClick={() => setSelectedTimeRange('last')}
              >
                {new Date().getFullYear() - 1}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compact mode controls - show only when compact */}
      {isCompact && (
        <div className="heatmap-controls compact">
          <div className="time-range-selector">
            <button 
              className={selectedTimeRange === 'current' ? 'active' : ''}
              onClick={() => setSelectedTimeRange('current')}
            >
              {new Date().getFullYear()}
            </button>
            <button 
              className={selectedTimeRange === 'last' ? 'active' : ''}
              onClick={() => setSelectedTimeRange('last')}
            >
              {new Date().getFullYear() - 1}
            </button>
          </div>
        </div>
      )}

      {/* Activity Summary */}
      <div className="activity-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-number">{activitySummary.totalActivities.toLocaleString()}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{activitySummary.avgDailyActivity}</span>
            <span className="stat-label">Daily Avg</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{activitySummary.maxDayActivity}</span>
            <span className="stat-label">Peak Day</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{Math.round((activitySummary.activeDays / activitySummary.totalDays) * 100)}%</span>
            <span className="stat-label">Active Days</span>
          </div>
        </div>
      </div>

      {/* Heatmap Visualization */}
      <div className="heatmap-container" style={{ minWidth: 0, width: '100%' }}>
        <div className="heatmap-months">
          {generateMonthLabels()}
        </div>
        
        {/* Force the flex container to respect boundaries */}
        <div className="heatmap-grid" style={{ display: 'flex', minWidth: 0, width: '100%' }}>
          <div className="day-labels" style={{ flexShrink: 0 }}>
            <span>Mon</span>
            <span>Wed</span>
            <span>Fri</span>
          </div>
          
          {/* The crucial fix: force the grid columns to shrink */}
          <div 
            className="heatmap-days" 
            style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(53, minmax(0, 1fr))', 
              gap: '3px', 
              flex: 1, 
              minWidth: 0 
            }}
          >
            {generateHeatmapDays()}
          </div>
        </div>
        
        <div className="heatmap-legend">
          <span>Less</span>
          <div className="legend-scale">
            <div className="legend-item level-0" title="No activity" style={{ backgroundColor: '#ebedf0' }}></div>
            <div className="legend-item level-1" title="1-3 activities" style={{ backgroundColor: '#c6e48b' }}></div>
            <div className="legend-item level-2" title="4-8 activities" style={{ backgroundColor: '#7bc96f' }}></div>
            <div className="legend-item level-3" title="9-15 activities" style={{ backgroundColor: '#239a3b' }}></div>
            <div className="legend-item level-4" title="16-25 activities" style={{ backgroundColor: '#196127' }}></div>
            <div className="legend-item level-5" title="25+ activities" style={{ backgroundColor: '#0d4016' }}></div>
          </div>
          <span>More</span>
        </div>
      </div>

      {/* Live Stats Display */}
      {isCompact && (
        <div className="live-stats" style={{ 
          marginTop: '1rem', 
          padding: '0.75rem', 
          background: '#f9fafb', 
          borderRadius: '0.5rem',
          fontSize: '0.75rem',
          color: '#6b7280',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Users: {users.length}</span>
            <span>Assets: {assets.length}</span>
            <span>Companies: {companies.length}</span>
          </div>
        </div>
      )}

      {/* Insights - Show only in non-compact mode */}
      {!isCompact && (
        <div className="activity-insights">
          <div className="insights-tabs">
            <button 
              className={selectedView === 'all' ? 'active' : ''}
              onClick={() => setSelectedView('all')}
            >
              Overview
            </button>
            <button 
              className={selectedView === 'users' ? 'active' : ''}
              onClick={() => setSelectedView('users')}
            >
              Top Users
            </button>
            <button 
              className={selectedView === 'companies' ? 'active' : ''}
              onClick={() => setSelectedView('companies')}
            >
              Companies
            </button>
          </div>

          <div className="insights-content">
            {selectedView === 'all' && (
              <div className="overview-stats">
                <div className="insight-card">
                  <h4>Live Platform Stats</h4>
                  <p>{users.length} total users across {companies.length} companies with {assets.length} assets</p>
                </div>
                <div className="insight-card">
                  <h4>Peak Activity</h4>
                  <p>Busiest day recorded {activitySummary.maxDayActivity} system actions</p>
                </div>
                <div className="insight-card">
                  <h4>Recent Growth</h4>
                  <p>System activity includes user registrations, asset additions, and daily usage patterns</p>
                </div>
              </div>
            )}

            {selectedView === 'users' && (
              <div className="top-users-list">
                <h4>Most Active Users</h4>
                <div className="users-grid">
                  {topUsers.length > 0 ? topUsers.map((user, index) => (
                    <div key={user.id} className="user-item">
                      <div className="user-rank">#{index + 1}</div>
                      <div className="user-info">
                        <span className="user-email">{user.email}</span>
                        <span className="user-activity">{user.count} activities</span>
                        <span className="user-last-seen">
                          Last: {user.lastActive?.toLocaleDateString() || 'Unknown'}
                        </span>
                      </div>
                      <div className="activity-bar">
                        <div 
                          className="activity-fill" 
                          style={{ 
                            width: `${Math.min((user.count / maxUserCount) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  )) : (
                    <p>No user activity data available</p>
                  )}
                </div>
              </div>
            )}

            {selectedView === 'companies' && (
              <div className="top-companies-list">
                <h4>Most Active Companies</h4>
                <div className="companies-grid">
                  {topCompanies.length > 0 ? topCompanies.map((company, index) => (
                    <div key={company.id} className="company-item">
                      <div className="company-rank">#{index + 1}</div>
                      <div className="company-info">
                        <span className="company-name">{company.name}</span>
                        <span className="company-activity">{company.count} activities</span>
                        <span className="company-last-seen">
                          Last: {company.lastActive?.toLocaleDateString() || 'Unknown'}
                        </span>
                      </div>
                      <div className="activity-bar">
                        <div 
                          className="activity-fill" 
                          style={{ 
                            width: `${Math.min((company.count / maxCompanyCount) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  )) : (
                    <p>No company activity data available</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityHeatmap;